import Phaser from 'phaser'
import { t } from '../../i18n'
import { apiAccountBattlepass, apiAccountProgress, apiClaimBattlepass, type BattlePassState, type AccountProgress, getHandle, missionLabel } from '../../services/JKGame_economy'
import { audio } from '../JKGame_audio'
import { COLORS } from '../JKGame_ui'
import { mountProgressOverlay, type ProgressOverlayHandle } from '../../ui/JKGame_progressOverlay'

// Dedicated season-progress page: season level, daily-mission progress, and the free BattlePass
// track with a claim action. Read-only over the same account endpoints Gacha/Result already use
// (so it's backend-account data) — degrades to a localized notice offline. Flag-agnostic (account
// endpoints, unrelated to the match path). Reached from the Menu's tappable season summary.
export class JKGame_ProgressScene extends Phaser.Scene {
  private alive = true // false after SHUTDOWN; guards async callbacks
  private busy = false // a claim is in flight
  private handle = ''
  private overlay?: ProgressOverlayHandle
  private readonly keyEsc = (): void => {
    this.scene.start('Menu')
  }

  constructor() {
    super('Progress')
  }

  create(): void {
    this.alive = true
    this.busy = false
    this.overlay?.destroy()
    this.overlay = undefined
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.alive = false
      this.overlay?.destroy()
      this.overlay = undefined
      this.input.keyboard?.off('keydown-ESC', this.keyEsc)
    })
    this.cameras.main.setBackgroundColor(COLORS.bg)
    this.cameras.main.fadeIn(240, 0, 0, 0)

    this.overlay = mountProgressOverlay({
      title: t('progress.title'),
      loadingText: t('menu.loading'),
      offlineText: t('progress.offline'),
      leaderboardLabel: t('leaderboard.open'),
      backLabel: t('menu.back'),
      onLeaderboard: () => this.scene.start('Leaderboard'),
      onBack: () => this.scene.start('Menu'),
      onClaim: () => void this.claim(),
    })
    this.input.keyboard?.off('keydown-ESC', this.keyEsc).on('keydown-ESC', this.keyEsc)

    void this.refresh()
  }

  private async refresh(): Promise<void> {
    try {
      this.handle = getHandle()
      const [prog, bp] = await Promise.all([apiAccountProgress(this.handle), apiAccountBattlepass(this.handle)])
      if (!this.alive) return
      this.render(prog, bp)
    } catch {
      if (!this.alive) return
      this.overlay?.setOffline() // never a silent blank
    }
  }

  private render(prog: AccountProgress, bp: BattlePassState): void {
    this.overlay?.render({
      seasonLine: t('result.seasonLv', { lv: prog.season.level }),
      dailyTitle: t('progress.daily'),
      missions: prog.missions.map((m) =>
        t('progress.mission', { name: missionLabel(m.id, m.title), cur: Math.min(m.current, m.goal), goal: m.goal }) + (m.done ? '　✓' : ''),
      ),
      bpTitle: t('bp.title'),
      bpTrack: t('bp.track', { claimed: bp.claimed.length, total: bp.season.level }),
      bpState: bp.claimable.length > 0 ? t('bp.claimable', { n: bp.claimable.length }) : t('bp.none'),
      claimLabel: t('bp.claim'),
      claimEnabled: bp.claimable.length > 0,
    })
  }

  private async claim(): Promise<void> {
    if (this.busy) return
    this.busy = true
    this.overlay?.setStatus(t('bp.claiming'), 'sub')
    try {
      const res = await apiClaimBattlepass(this.handle)
      if (!this.alive) return
      audio.play('success')
      const items = res.claimed.filter((c) => c.reward.item).length
      this.overlay?.setStatus(t('bp.gained', { coin: res.gained.coin, items }), 'good')
      await this.refresh() // refresh the track + season + missions after claiming
    } catch {
      if (!this.alive) return
      audio.play('error')
      this.overlay?.setStatus(t('progress.offline'), 'bad')
    } finally {
      this.busy = false
    }
  }
}
